// Shrinkwrap season logic shared by the Work Orders › Shrinkwrap view, the Dashboard card,
// the op panel and the techs app. Pure (no Firebase), so tests/unit/shrinkwrap.test.mjs runs it.
//
// Stages of a shrinkwrap install op (Op_Type 'Shrinkwrap', not a removal):
//   Not hauled → Waiting on work → Ready to Frame → Framed → Vacuumed → Covered → Tightened
//   → Awaiting Review (vents installed) → Done
// The first three are worked out from the boat and the work order, not read from the op:
// "Ready to Frame" means the boat is out of the water and every other op on the winterizing
// work order is finished. From Framed on, the op's own Status (written by the stage track) wins.
//
// Wrap size on the boat record:
//   Shrinkwrap_Width, Shrinkwrap_Length  feet actually used last time (numbers)
//   Shrinkwrap_Date, Shrinkwrap_By, Shrinkwrap_WO  when / who / which work order recorded it

/** Standard film widths stocked, in feet (narrowest first). */
export const SHRINK_WIDTHS = [16, 20, 24, 28, 32, 40];
/** Extra length past the bow and past the stern, in feet. */
export const END_ALLOWANCE_FT = 5;
/** Film goes up over the frame and down both sides: about twice the beam, plus 2 ft a side to tuck under the strap line. */
export const SIDE_TUCK_FT = 2;

export const STAGES = [
  { key: 'not-hauled', label: 'Not hauled' },
  { key: 'waiting', label: 'Waiting on work' },
  { key: 'ready', label: 'Ready to Frame' },
  { key: 'framed', label: 'Framed' },
  { key: 'vacuumed', label: 'Vacuumed' },
  { key: 'covered', label: 'Covered' },
  { key: 'tightened', label: 'Tightened' },
  { key: 'review', label: 'Awaiting Review' },
  { key: 'done', label: 'Done' },
];
const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));
/** Stages that still need film cut (everything before Covered). */
export const NEEDS_WRAP_STAGES = ['not-hauled', 'waiting', 'ready', 'framed', 'vacuumed'];

// Op statuses written by the stage track (app.js shrinkwrapLabor) → stage key.
const STATUS_STAGE = {
  Framed: 'framed',
  Vacuumed: 'vacuumed',
  Covered: 'covered',
  Tightened: 'tightened',
  'Vents Installed': 'review',
  'Awaiting Review': 'review',
};
const DONE_STATUSES = ['Ready to Bill', 'Complete', 'Billed', 'Closed'];
const OUT_OF_WATER = ['yard', 'rack', 'trailer'];

// Installs and removals share Op_Type 'Shrinkwrap'; only an install has the
// frame/vacuum/cover stage track. A removal says so in its description.
export function isShrinkwrapInstall(op) {
  if (!op || op.Op_Type !== 'Shrinkwrap') return false;
  const text = `${op.Short_Description || ''} ${op.Description || ''} ${op.Short_Description_DM || ''}`;
  return !/remov/i.test(text);
}

/** Feet from "28", 28, "28'", "28 ft", "28' 6\"", "28'6", "28.5". Null when there's no number. */
export function parseFeet(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  const s = String(value).trim();
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft\.?|feet|foot)?\s*(?:(\d+(?:\.\d+)?)\s*(?:"|''|in\.?|inches)?)?$/i);
  if (!m) {
    const n = parseFloat(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const feet = parseFloat(m[1]) + (m[2] ? parseFloat(m[2]) / 12 : 0);
  return feet > 0 ? feet : null;
}

/** The narrowest standard width that covers this beam; `over` when even 40 ft is short (seam two pieces). */
export function widthForBeam(beamFt) {
  const beam = parseFeet(beamFt);
  if (!beam) return null;
  const needed = 2 * beam + 2 * SIDE_TUCK_FT;
  const width = SHRINK_WIDTHS.find(w => w >= needed);
  return width ? { width, over: false } : { width: SHRINK_WIDTHS[SHRINK_WIDTHS.length - 1], over: true };
}

/** Suggested cut for a boat: width from the beam, length = LOA + 5 ft at the bow and 5 at the stern. */
export function estimateWrap(boat) {
  if (!boat) return null;
  const loa = parseFeet(boat.Boat_Length);
  const beam = parseFeet(boat.Boat_Beam);
  if (!loa || !beam) return null;
  const w = widthForBeam(beam);
  return { width: w.width, length: Math.ceil(loa + 2 * END_ALLOWANCE_FT), over: w.over, loa, beam };
}

/** What was actually used last time, from the boat record. */
export function recordedWrap(boat) {
  if (!boat) return null;
  const width = parseFeet(boat.Shrinkwrap_Width);
  const length = parseFeet(boat.Shrinkwrap_Length);
  if (!width || !length) return null;
  return { width, length, date: boat.Shrinkwrap_Date || '', by: boat.Shrinkwrap_By || '', workOrder: boat.Shrinkwrap_WO || '' };
}

/** The size to plan with: last time's if recorded, else the estimate. */
export function planWrap(boat) {
  const rec = recordedWrap(boat);
  if (rec) return { width: rec.width, length: rec.length, source: 'recorded' };
  const est = estimateWrap(boat);
  if (est) return { width: est.width, length: est.length, source: 'estimate', over: est.over };
  return null;
}

const trimNum = n => (Math.round(n * 10) / 10).toString();
/** 24' × 45' */
export function fmtWrap(w) {
  if (!w || !w.width || !w.length) return '';
  return `${trimNum(w.width)}' × ${trimNum(w.length)}'`;
}

/** The shrinkwrap season runs from Aug 1 to Jul 31 (wrap goes on in the fall, comes off in spring). */
export function seasonStart(today) {
  const [y, m] = String(today).split('-').map(Number);
  return `${m >= 8 ? y : y - 1}-08-01`;
}

function opDay(op) {
  return String(op.Schedule_Date || op.Start_Date || op.Request_Date || '').slice(0, 10);
}

/** Out of the water: hauled this season and not launched since, or the boat's location says so. */
export function isHauled(boat, today) {
  if (!boat) return false;
  const start = seasonStart(today);
  const hauled = String(boat.Date_Hauled || '').slice(0, 10);
  const launched = String(boat.Date_Launched || '').slice(0, 10);
  if (hauled && hauled >= start && !(launched && launched > hauled)) return true;
  return OUT_OF_WATER.includes(boat.Boat_Location_Status);
}

function boatOf(customersById, op) {
  const customer = customersById.get(op.Customer_ID);
  if (!customer || !customer.Boats || !op.Boat_ID) return null;
  return customer.Boats[op.Boat_ID] || Object.values(customer.Boats).find(b => b && b.Boat_ID === op.Boat_ID) || null;
}

const opTitle = op => op.Short_Description_DM || op.Short_Description || op.Description || String(op.OpCode || 'Operation');

/**
 * One row per shrinkwrap install op this season, with its stage.
 * jobs: jobData rows (each with id); customersById: Map(Customer_ID → customer record).
 */
export function buildShrinkwrapBoard(jobs, customersById, today) {
  const start = seasonStart(today);
  const live = (jobs || []).filter(j => j && j.id && j.id !== 'lastUpdated');
  const installs = live.filter(j => isShrinkwrapInstall(j) && j.Status !== 'Declined'
    && (!DONE_STATUSES.includes(j.Status) || opDay(j) >= start));

  const byWorkOrder = new Map();
  const winterByBoat = new Map();
  live.forEach(j => {
    if (!byWorkOrder.has(j.Work_Order)) byWorkOrder.set(j.Work_Order, []);
    byWorkOrder.get(j.Work_Order).push(j);
    if (j.Category === 'Winterizing' && j.Boat_ID && opDay(j) >= start) {
      if (!winterByBoat.has(j.Boat_ID)) winterByBoat.set(j.Boat_ID, []);
      winterByBoat.get(j.Boat_ID).push(j);
    }
  });

  return installs.map(op => {
    const boat = boatOf(customersById, op);
    // Every other op on this work order, plus this season's winterizing ops on the same boat's other work orders.
    const related = new Map();
    (byWorkOrder.get(op.Work_Order) || []).forEach(j => related.set(j.id, j));
    (winterByBoat.get(op.Boat_ID) || []).forEach(j => related.set(j.id, j));
    const hauled = isHauled(boat, today);
    // Once the boat is out, its haul op still being open is paperwork, not work in the way of the frame.
    const blockers = [...related.values()].filter(j => j.id !== op.id && j.Op_Type !== 'Shrinkwrap'
      && !(hauled && j.Op_Type === 'Hauling')
      && j.Status !== 'Declined' && !DONE_STATUSES.includes(j.Status));

    let stage;
    if (DONE_STATUSES.includes(op.Status)) stage = 'done';
    else if (STATUS_STAGE[op.Status]) stage = STATUS_STAGE[op.Status];
    else if (!hauled) stage = 'not-hauled';
    else if (blockers.length) stage = 'waiting';
    else stage = 'ready';

    return {
      id: op.id,
      workOrder: op.Work_Order || '',
      customerId: op.Customer_ID,
      boatId: op.Boat_ID,
      boatName: (boat && boat.Boat_Name) || op.Boat_Name || '',
      customer: op.Customer_Name || '',
      status: op.Status || '',
      hold: op.Status === 'Hold',
      stage,
      stageIndex: STAGE_INDEX[stage],
      blockers: blockers.map(j => ({ id: j.id, title: opTitle(j), status: j.Status || '' })),
      hauled,
      haulDate: (boat && boat.Haul_Date) || '',
      loa: boat ? parseFeet(boat.Boat_Length) : null,
      beam: boat ? parseFeet(boat.Boat_Beam) : null,
      boatType: (boat && boat.Boat_Type) || '',
      recorded: recordedWrap(boat),
      estimate: estimateWrap(boat),
      plan: planWrap(boat),
      techs: op.Techs || '',
      scheduleDate: op.Schedule_Date || '',
      title: opTitle(op),
    };
  });
}

/**
 * Film still to cut this season, by standard width: every row before Covered.
 * A recorded size that isn't a standard width is counted under the next standard width up.
 * → { widths: [{ width, feet, boats }], totalFeet, boats, unknown: [rows with no size] }
 */
export function wrapTotals(rows) {
  const per = new Map(SHRINK_WIDTHS.map(w => [w, { width: w, feet: 0, boats: 0 }]));
  const unknown = [];
  let totalFeet = 0;
  let boats = 0;
  (rows || []).filter(r => NEEDS_WRAP_STAGES.includes(r.stage)).forEach(r => {
    if (!r.plan) { unknown.push(r); return; }
    const width = SHRINK_WIDTHS.find(w => w >= r.plan.width) || SHRINK_WIDTHS[SHRINK_WIDTHS.length - 1];
    const slot = per.get(width);
    slot.feet += r.plan.length;
    slot.boats += 1;
    totalFeet += r.plan.length;
    boats += 1;
  });
  return { widths: [...per.values()], totalFeet, boats, unknown };
}

/** Counts per stage key. */
export function stageCounts(rows) {
  const counts = Object.fromEntries(STAGES.map(s => [s.key, 0]));
  (rows || []).forEach(r => { counts[r.stage] = (counts[r.stage] || 0) + 1; });
  return counts;
}

/** The "Total shrinkwrap needed" panel: Work Orders › Shrinkwrap and the Dashboard card (styles/shrinkwrap-totals.css). */
export function wrapTotalsHTML(totals, { compact = false } = {}) {
  const max = Math.max(1, ...totals.widths.map(w => w.feet));
  const rows = totals.widths.map(w => `
    <div class="sw-total-row${w.feet ? '' : ' sw-total-row--zero'}" data-key="w-${w.width}">
      <span class="sw-total-width">${w.width}'</span>
      <span class="sw-total-bar"><span style="width:${Math.round((w.feet / max) * 100)}%"></span></span>
      <span class="sw-total-feet">${w.feet.toLocaleString('en-US')} ft</span>
      <span class="sw-total-boats">${w.boats} boat${w.boats === 1 ? '' : 's'}</span>
    </div>`).join('');
  return `
    <div class="sw-total-head">
      <span class="sw-total-sum">${totals.totalFeet.toLocaleString('en-US')} ft</span>
      <span class="sw-total-sub">for ${totals.boats} boat${totals.boats === 1 ? '' : 's'} not covered yet</span>
    </div>
    <div class="sw-total-cols"><span>Width</span><span></span><span>Length</span><span>Boats</span></div>
    ${rows}
    ${totals.unknown.length ? `<div class="sw-total-unknown" data-key="unknown">${totals.unknown.length} boat${totals.unknown.length === 1 ? ' has' : 's have'} no length or beam on file, so ${totals.unknown.length === 1 ? 'it isn\'t' : 'they aren\'t'} counted.</div>` : ''}
    ${compact ? '' : `<div class="sw-total-note">Uses the size recorded last time when there is one. Otherwise: length overall + ${END_ALLOWANCE_FT} ft at the bow and ${END_ALLOWANCE_FT} ft at the stern, and width of 2 × beam + ${2 * SIDE_TUCK_FT} ft, rounded up to the next stock width.</div>`}`;
}
